"use client";

import { useState, useRef, useEffect } from "react";
import { FilterConfig, SelectedMap } from "./Types";
import FilterDropdown from "./FilterDropdown";
import { FILTER_SORT_OPTIONS } from "@/app/utilities/constants";
import Image from "next/image";

// ─── Icons ────────────────────────────────────────────────────────────────────
const ChevronDown: React.FC<{ size?: number }> = ({ size = 14 }) => (
	<svg
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2.5"
	>
		<polyline points="6 9 12 15 18 9" />
	</svg>
);

const FilterIcon: React.FC = () => <Image
				src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}fillter-icon.svg`}
				alt="fillter icon"
				title="fillter icon"
				width={16}
				height={16}
			/>;

const CheckIcon: React.FC<{ size?: number }> = ({ size = 13 }) => (
	<Image
		src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}check.svg`}
		alt="check"
		title="check"
		width={size}
		height={size}
	/>
);

const ArrowRightIcon: React.FC<{ size?: number }> = ({ size = 12 }) => (
	<svg
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2.5"
		className="inline-block mx-1 align-[-1px]"
	>
		<line x1="4" y1="12" x2="20" y2="12" />
		<polyline points="14 6 20 12 14 18" />
	</svg>
);

// ─── Props ────────────────────────────────────────────────────────────────────
interface CommonFilterProps {
	selectedMap: SelectedMap;
	onSelect: (
		filterKey: string,
		value: string,
		type: "single" | "multi",
	) => void;
	sortValue: string;
	onSortChange: (value: string) => void;
	filterConfigs: FilterConfig[];
	applyFilterTrackingClass?: string;
	sortAppliedTrackingClass?: string;
	is_latest?: boolean | undefined;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function CommonFilter({
	selectedMap,
	onSelect,
	sortValue,
	onSortChange,
	filterConfigs,
	applyFilterTrackingClass = '',
	sortAppliedTrackingClass = '',
	is_latest = false
}: CommonFilterProps) {
	const [openFilter, setOpenFilter] = useState<string | null>(null);
	const [sortOpen, setSortOpen] = useState<boolean>(false);
	const sortRef = useRef<HTMLDivElement>(null);

	// Close sort on outside click
	useEffect(() => {
		const handler = (e: MouseEvent) => {
			if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
				setSortOpen(false);
			}
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, []);

	const currentSort = FILTER_SORT_OPTIONS?.find((o) => o.value === sortValue);
	const sortLabel = currentSort?.outLabel;

	const filtersToShow = is_latest ? FILTER_SORT_OPTIONS.filter(item=>item.value!="sort_order_asc") : FILTER_SORT_OPTIONS.filter(item=>item.value!="launch_date_desc");

	return (
		<div className="flex items-center gap-2.5 bg-[#f9fafb] py-4 px-3 rounded-[14px] flex-wrap [.fillterTwoSection_&]:items-baseline">
			{/* Filter icon */}
			<div className="font-normal text-[12px] leading-4 uppercase tracking-[0.07em] text-[#99A1AF] flex items-center gap-2 whitespace-nowrap">
				<FilterIcon />
				<span>Filter</span>
				<span className="text-[#d1d5db]">|</span>
			</div>

			{/* Pills */}
			<div className="flex items-center gap-2 flex-wrap flex-1 [.fillterTwoSection_&]:gap-1.5">
				{filterConfigs?.map((filter) => (
					<FilterDropdown
						key={filter.key}
						filter={filter}
						openFilter={openFilter}
						setOpenFilter={setOpenFilter}
						selectedMap={selectedMap}
						onSelect={onSelect}
						applyFilterTrackingClass={applyFilterTrackingClass}
					/>
				))}
			</div>

			{/* Sort */}
			<div ref={sortRef} className="ml-auto relative">
				<button
					className="flex items-center gap-[7px] py-1.5 px-3 border-[1.5px] border-[#008C8B] rounded-[999px] bg-[#F2F9F9] text-[#008C8B] text-[12px] font-medium cursor-pointer whitespace-nowrap transition-[background] duration-150 ease-[ease] hover:bg-[#f0fdfb] [.fillterTwoSection_&]:gap-[5px] [.fillterTwoSection_&]:px-2"
					onClick={() => setSortOpen((prev) => !prev)}
					type="button"
				>
					{sortLabel}
					<ChevronDown />
				</button>

				<div
					className={`absolute top-[calc(100%+18px)] right-0 bg-white border-[1.5px] border-[#e5e7eb] rounded-xl min-w-[210px] shadow-[0px_4px_4px_0px_#00000040] z-[100] overflow-hidden py-1.5 ${sortOpen ? "block" : "hidden"}`}
				>
					<div className="pt-2 px-3.5 pb-3 text-[10px] font-bold tracking-[0.08em] text-[#9ca3af] uppercase">Sort By</div>

					{filtersToShow.map((opt) => {
						const isActive = sortValue === opt.value;
						return (
							<div
								key={opt.value}
								className={`flex items-center justify-between py-[11px] px-3.5 cursor-pointer text-[12px] leading-5 transition-[background] duration-120 ease-[ease] ${isActive ? "bg-[#008C8B] text-white font-medium rounded-lg mx-1.5 hover:bg-[#008C8B]" : "font-normal text-[#364153] hover:bg-[#f9fafb]"} ${sortAppliedTrackingClass}`}
								onClick={() => {
									onSortChange(opt.value);
									setSortOpen(false);
								}}
							>
								<span className="flex items-center">
									{opt.label}
									{opt.labelTo && <><ArrowRightIcon />{opt.labelTo}</>}
								</span>
								{isActive && <CheckIcon />}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
